import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useEntityList, useEntitySave, now } from '../api/hooks/useEntity'
import { ENTITY } from '../types/entities'
import type { Notification } from '../types/entities'

/**
 * Иконка-колокольчик с числом непрочитанных уведомлений и dropdown'ом
 * с последними 5. Клик по уведомлению — отметка прочитанным + переход по link.
 */
export function BellIcon() {
  const { t } = useTranslation()
  const list = useEntityList<Notification>(ENTITY.notification)
  const save = useEntitySave<Notification>(ENTITY.notification)
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  const { unreadCount, recent } = useMemo(() => {
    const all = (list.data ?? [])
      .filter((n) => !n.deletedAt)
      .sort((a, b) => b.createdAt - a.createdAt)
    return {
      unreadCount: all.filter((n) => !n.readAt).length,
      recent: all.slice(0, 5),
    }
  }, [list.data])

  const markRead = (n: Notification) => {
    if (n.readAt) return
    save.mutate({ ...n, readAt: now(), updatedAt: now() })
  }

  const handleClick = (n: Notification) => {
    markRead(n)
    setOpen(false)
    if (n.link) navigate(n.link)
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        className="relative p-2 text-gray-600 hover:text-primary transition-colors"
        aria-label={t('bellIcon.title')}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl w-[320px] max-w-[calc(100vw-1rem)] z-40 animate-fade-in">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
            <span className="font-semibold text-sm text-gray-900">{t('bellIcon.title')}</span>
            {unreadCount > 0 && (
              <span className="text-xs text-primary font-medium">{t('bellIcon.unread', { n: unreadCount })}</span>
            )}
          </div>

          {recent.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">
              {t('bellIcon.empty')}
            </div>
          ) : (
            <div className="max-h-[400px] overflow-y-auto">
              {recent.map((n) => (
                <button
                  key={n.id}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleClick(n)}
                  className={`block w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                    !n.readAt ? 'bg-primary-soft/30' : ''
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-base flex-shrink-0">{iconFor(n.type)}</span>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm leading-tight ${!n.readAt ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                        {n.title}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                        {n.body}
                      </div>
                      <div className="text-[10px] text-gray-400 mt-1">{formatRel(n.createdAt, t)}</div>
                    </div>
                    {!n.readAt && (
                      <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          <Link
            to="/app/notifications"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setOpen(false)}
            className="block py-2.5 text-center text-sm text-primary font-medium border-t border-gray-100 hover:bg-gray-50"
          >
            {t('bellIcon.viewAll')} →
          </Link>
        </div>
      )}
    </div>
  )
}

export function iconFor(type: Notification['type']): string {
  return {
    template_published: '🧩',
    template_pending_review: '📥',
    template_approved: '✅',
    template_rejected: '❌',
    school_invite_accepted: '🤝',
    school_join: '🏫',
    low_llm_limit: '⚠️',
    student_alert: '👤',
    system: '⚙️',
  }[type] ?? '📬'
}

export function formatRel(ts: number, t?: TFunction): string {
  const diff = Date.now() - ts
  if (diff < 60_000) return t ? t('bellIcon.justNow') : 'только что'
  if (diff < 3600_000) return t ? t('bellIcon.minAgo', { n: Math.floor(diff / 60_000) }) : `${Math.floor(diff / 60_000)} мин назад`
  if (diff < 86400_000) return t ? t('bellIcon.hourAgo', { n: Math.floor(diff / 3600_000) }) : `${Math.floor(diff / 3600_000)} ч назад`
  if (diff < 30 * 86400_000) return t ? t('bellIcon.dayAgo', { n: Math.floor(diff / 86400_000) }) : `${Math.floor(diff / 86400_000)} дн назад`
  return new Date(ts).toLocaleDateString('ru')
}
